<template>
  <div>
    <subject-list-add-new
      :is-add-new-subject-sidebar-active.sync="isAddNewSubjectSidebarActive"
      :visibility-options="visibilityOptions"
      :status-options="statusOptions"
      @refetch-data="refetchData"
    />

    <!-- Filters -->
    <subject-list-filters
      :visibility-filter.sync="visibilityFilter"
      :status-filter.sync="statusFilter"
      :visibility-options="visibilityOptions"
      :status-options="statusOptions"
    />

    <!-- Table Container Card -->
    <b-card no-body class="mb-0">
      <div class="m-2">
        <!-- Table Top -->
        <b-row>
          <!-- Per Page -->
          <b-col cols="12" md="6" class="d-flex align-items-center justify-content-start mb-1 mb-md-0">
            <label>Show</label>
            <v-select
              v-model="perPage"
              :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
              :options="perPageOptions"
              :clearable="false"
              class="per-page-selector d-inline-block mx-50"
            />
            <label>entries</label>
          </b-col>

          <!-- Search -->
          <b-col cols="12" md="6">
            <div class="d-flex align-items-center justify-content-end">
              <b-form-input v-model="searchQuery" class="d-inline-block mr-1" placeholder="Search..." />
              <b-button variant="primary" @click="isAddNewSubjectSidebarActive = true">
                <span class="text-nowrap">Add Subject</span>
              </b-button>
            </div>
          </b-col>
        </b-row>
      </div>

      <b-table
        ref="refSubjectListTable"
        class="position-relative"
        :items="fetchSubjects"
        responsive
        :fields="tableColumns"
        primary-key="id"
        :sort-by.sync="sortBy"
        show-empty
        empty-text="No matching records found"
        :sort-desc.sync="isSortDirDesc"
      >
        <!-- Column: Subject -->
        <template #cell(name)="data">
          <b-media vertical-align="center">
            <template #aside>
              <b-avatar
                size="32"
                :src="data.item.avatar"
                :text="avatarText(data.item.name)"
                :variant="`light-${resolveSubjectStatusVariant(data.item.status)}`"
                :to="{ name: 'apps-roster-subject-view', params: { id: data.item.id } }"
                rounded
              />
            </template>
            <b-link
              class="font-weight-bold d-block text-nowrap"
              :to="{ name: 'apps-roster-subject-view', params: { id: data.item.id } }"
            >
              {{ data.item.name }}
            </b-link>
            <small class="text-muted">{{ (data.item.instructors || []).length }} total instructors</small>
          </b-media>
        </template>

        <!-- Column: Live Sessions -->
        <template #cell(live_sessions)="data">
          <div class="text-nowrap">
            <span class="align-text-top text-capitalize">{{
              data.item.sessions.filter((session) => session.status === 'live').length
            }}</span>
          </div>
        </template>

        <!-- Column: Visibility -->
        <template #cell(visibility)="data">
          <b-badge pill :variant="`light-${resolveSubjectVisibilityVariant(data.item.visibility)}`" class="text-capitalize">
            {{ data.item.visibility.split('_').join(' ') }}
          </b-badge>
        </template>

        <!-- Column: Status -->
        <template #cell(status)="data">
          <b-badge pill :variant="`light-${resolveSubjectStatusVariant(data.item.status)}`" class="text-capitalize">
            {{ data.item.status.split('_').join(' ') }}
          </b-badge>
        </template>

        <!-- Column: Actions -->
        <template #cell(actions)="data">
          <b-dropdown variant="link" no-caret :right="$store.state.appConfig.isRTL">
            <template #button-content>
              <feather-icon icon="MoreVerticalIcon" size="16" class="align-middle text-body" />
            </template>

            <b-dropdown-item :to="{ name: 'apps-roster-session-builder', query: { subject: data.item.id } }">
              <feather-icon icon="PlusIcon" />
              <span class="align-middle ml-50">Create Session</span>
            </b-dropdown-item>
            <b-dropdown-item :to="{ name: 'apps-roster-subject-edit', params: { id: data.item.id } }">
              <feather-icon icon="EditIcon" />
              <span class="align-middle ml-50">Edit</span>
            </b-dropdown-item>
            <b-dropdown-item @click="deleteSubject(data.item)">
              <feather-icon icon="TrashIcon" />
              <span class="align-middle ml-50">Remove</span>
            </b-dropdown-item>
          </b-dropdown>
        </template>
      </b-table>
      <div class="mx-2 mb-2">
        <b-row>
          <b-col cols="12" sm="6" class="d-flex align-items-center justify-content-center justify-content-sm-start">
            <span class="text-muted">Showing {{ dataMeta.from }} to {{ dataMeta.to }} of {{ dataMeta.of }} entries</span>
          </b-col>
          <!-- Pagination -->
          <b-col cols="12" sm="6" class="d-flex align-items-center justify-content-center justify-content-sm-end">
            <b-pagination
              v-model="currentPage"
              :total-rows="totalSubjects"
              :per-page="perPage"
              first-number
              last-number
              class="mb-0 mt-1 mt-sm-0"
              prev-class="prev-item"
              next-class="next-item"
            >
              <template #prev-text>
                <feather-icon icon="ChevronLeftIcon" size="18" />
              </template>
              <template #next-text>
                <feather-icon icon="ChevronRightIcon" size="18" />
              </template>
            </b-pagination>
          </b-col>
        </b-row>
      </div>
    </b-card>
  </div>
</template>

<script>
import {
  BCard,
  BRow,
  BCol,
  BFormInput,
  BButton,
  BTable,
  BMedia,
  BAvatar,
  BLink,
  BBadge,
  BDropdown,
  BDropdownItem,
  BPagination,
} from 'bootstrap-vue';
import vSelect from 'vue-select';
import store from '@/store';
import { ref, onUnmounted } from '@vue/composition-api';
import { avatarText } from '@core/utils/filter';
import SubjectListFilters from './SubjectListFilters.vue';
import useSubjectsList from './useSubjectsList';
import rosterStoreModule from '../rosterStoreModule';
import SubjectListAddNew from './SubjectListAddNew.vue';

export default {
  components: {
    SubjectListFilters,
    SubjectListAddNew,

    BCard,
    BRow,
    BCol,
    BFormInput,
    BButton,
    BTable,
    BMedia,
    BAvatar,
    BLink,
    BBadge,
    BDropdown,
    BDropdownItem,
    BPagination,

    vSelect,
  },
  methods: {
    editSubject(data) {},
    deleteSubject(data) {
      store.dispatch('app-roster/deleteSubject', { id: data.id }).then(() => {
        this.refetchData();
      });
    },
  },
  setup() {
    const SUBJECT_APP_STORE_MODULE_NAME = 'app-roster';

    // Register module
    if (!store.hasModule(SUBJECT_APP_STORE_MODULE_NAME))
      store.registerModule(SUBJECT_APP_STORE_MODULE_NAME, rosterStoreModule);

    // UnRegister on leave
    onUnmounted(() => {
      if (store.hasModule(SUBJECT_APP_STORE_MODULE_NAME)) store.unregisterModule(SUBJECT_APP_STORE_MODULE_NAME);
    });

    const isAddNewSubjectSidebarActive = ref(false);

    const {
      fetchSubjects,
      tableColumns,
      perPage,
      currentPage,
      totalSubjects,
      dataMeta,
      perPageOptions,
      searchQuery,
      sortBy,
      isSortDirDesc,
      refSubjectListTable,
      refetchData,

      // UI
      resolveSubjectRoleVariant,
      resolveSubjectRoleIcon,
      resolveSubjectVisibilityVariant,
      resolveSubjectStatusVariant,

      visibilityOptions,
      statusOptions,

      // Extra Filters
      visibilityFilter,
      statusFilter,
    } = useSubjectsList();

    return {
      // Sidebar
      isAddNewSubjectSidebarActive,

      fetchSubjects,
      tableColumns,
      perPage,
      currentPage,
      totalSubjects,
      dataMeta,
      perPageOptions,
      searchQuery,
      sortBy,
      isSortDirDesc,
      refSubjectListTable,
      refetchData,

      // Filter
      avatarText,

      // UI
      resolveSubjectRoleVariant,
      resolveSubjectRoleIcon,
      resolveSubjectVisibilityVariant,
      resolveSubjectStatusVariant,

      visibilityOptions,
      statusOptions,

      // Extra Filters
      visibilityFilter,
      statusFilter,
    };
  },
};
</script>

<style lang="scss" scoped>
.per-page-selector {
  width: 90px;
}
</style>

<style lang="scss">
@import '@core/scss/vue/libs/vue-select.scss';
</style>
